import {
  InvoiceStatus,
  IWorkLog,
  IWorkLogCreate,
  IWorkLogUpdate,
  WorkLogStatus
} from "@wingspanhq/payments/dist/interfaces";

import { WSService } from "../utils/WSService";
import { ApiSortDirection } from "../utils/serviceHelper";

import { servicePayments } from "./servicePayments";

const service = new WSService("/payments/work-log");

export async function createWorkLog(
  request: IWorkLogCreate
): Promise<IWorkLog> {
  const { data } = await service.post("", request);
  return data;
}

export type ApiQueryListWorkLogs = {
  // workLogId filters
  "filter[workLogId][=]"?: string;
  "filter[workLogId][!=]"?: string;
  "filter[workLogId][in]"?: string[];
  "filter[workLogId][notIn]"?: string[];

  // workLogNumber filters
  "filter[workLogNumber][=]"?: string;
  "filter[workLogNumber][!=]"?: string;
  "filter[workLogNumber][in]"?: string[];
  "filter[workLogNumber][notIn]"?: string[];

  // status filters
  "filter[status][=]"?: WorkLogStatus;
  "filter[status][!=]"?: WorkLogStatus;
  "filter[status][in]"?: WorkLogStatus[];
  "filter[status][notIn]"?: WorkLogStatus[];

  // payerId filters
  "filter[payerId]"?: string;

  // payeeId filters
  "filter[payeeId]"?: string;
  "filter[payeeId][in]"?: string[];
  "filter[payeeId][notIn]"?: string[];

  // payerPayeeEngagementId filters
  "filter[payerPayeeEngagementId][=]"?: string;
  "filter[payerPayeeEngagementId][in]"?: string[];

  // engagementId filters
  "filter[engagementId][=]"?: string;
  "filter[engagementId][in]"?: string[];

  // startDate filters
  "filter[startDate][=]"?: Date;
  "filter[startDate][>]"?: Date;
  "filter[startDate][>=]"?: Date;
  "filter[startDate][<]"?: Date;
  "filter[startDate][<=]"?: Date;

  // endDate filters
  "filter[endDate][=]"?: Date;
  "filter[endDate][>]"?: Date;
  "filter[endDate][>=]"?: Date;
  "filter[endDate][<]"?: Date;
  "filter[endDate][<=]"?: Date;
  "filter[endDate][exists]"?: boolean;

  // createdAt filters
  "filter[createdAt][=]"?: Date;
  "filter[createdAt][>]"?: Date;
  "filter[createdAt][>=]"?: Date;
  "filter[createdAt][<]"?: Date;
  "filter[createdAt][<=]"?: Date;

  // updatedAt filters
  "filter[updatedAt][=]"?: Date;
  "filter[updatedAt][>]"?: Date;
  "filter[updatedAt][>=]"?: Date;
  "filter[updatedAt][<]"?: Date;
  "filter[updatedAt][<=]"?: Date;

  // Sorting
  "sort[createdAt]"?: ApiSortDirection;
  "sort[updatedAt]"?: ApiSortDirection;
  "sort[startDate]"?: ApiSortDirection;
  "sort[endDate]"?: ApiSortDirection;

  page?: {
    size?: number;
    number?: number;
  };
};

export async function listWorkLogs(
  query?: ApiQueryListWorkLogs
): Promise<IWorkLog[]> {
  const { data } = await service.get("", { params: query });
  return data;
}

export type IWorkLogsSummary = {
  listSize: number;
  totalAmount: number;
};

export async function getWorkLogsSummary(
  query?: ApiQueryListWorkLogs
): Promise<IWorkLogsSummary> {
  const { headers } = await service.get("", { params: query });

  return {
    listSize: Number(headers["x-ws-list-size"]),
    totalAmount: Number(headers["x-ws-custom-worklog-amount-total"])
  };
}

export async function getWorkLog(workLogId: string): Promise<IWorkLog> {
  const { data } = await service.get("/" + workLogId);
  return data;
}

export type ApiQueryConvertWorkLog = {
  invoiceStatus?: InvoiceStatus;
};

export async function convertWorkLog(
  workLogId: string,
  query?: ApiQueryConvertWorkLog
): Promise<IWorkLog> {
  const { data } = await service.post("/" + workLogId + "/convert", undefined, {
    params: query
  });
  return data;
}

export async function updateWorkLog(
  workLogId: string,
  request: IWorkLogUpdate
): Promise<IWorkLog> {
  const { data } = await service.patch("/" + workLogId, request);
  return data;
}

// Work log review workflow actions
// All status transitions use PATCH /work-log/:workLogId with { status, payeeOwnedData?, payerOwnedData? }

export type SubmitWorkLogForReviewRequest = {
  workLogId: string;
};

// Payee action: Draft/InProgress → InReview
// LOGIC: explicitly clear payeeOwnedData.comment to avoid stale editing
// requests persisting across resubmissions. The backend transitionStatus should
// clear owned data on status transitions but currently doesn't (TODO: fix in BE).
export async function submitWorkLogForReview(
  request: SubmitWorkLogForReviewRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    status: "InReview" as WorkLogStatus,
    payeeOwnedData: { comment: null }
  });
  return data;
}

export type ApproveWorkLogRequest = {
  workLogId: string;
  comment?: string;
};

// Payer action: InReview → Approved
export async function approveWorkLog(
  request: ApproveWorkLogRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    status: "Approved" as WorkLogStatus,
    ...(request.comment ? { payerOwnedData: { comment: request.comment } } : {})
  });
  return data;
}

export type RejectWorkLogRequest = {
  workLogId: string;
  comment: string;
};

// Payer action: InReview → Rejected (comment required)
export async function rejectWorkLog(
  request: RejectWorkLogRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    status: "Rejected" as WorkLogStatus,
    payerOwnedData: { comment: request.comment }
  });
  return data;
}

export type RequestWorkLogChangesRequest = {
  workLogId: string;
  comment: string;
};

// Payer action: InReview → InProgress
export async function requestWorkLogChanges(
  request: RequestWorkLogChangesRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    status: "InProgress" as WorkLogStatus,
    payerOwnedData: { comment: request.comment }
  });
  return data;
}

export type ReopenWorkLogRequest = {
  workLogId: string;
  invoiceId: string;
  targetStatus: "InReview" | "InProgress";
  comment?: string;
};

// Payer action: Reopen work log (Approved → InReview or InProgress)
// Cancels the linked invoice via the payable endpoint; the BE's invoice
// cancellation handler transitions the work log to targetStatus.
export async function reopenWorkLog(
  request: ReopenWorkLogRequest
): Promise<IWorkLog> {
  const { data } = await servicePayments.patch(
    `/payable/${request.invoiceId}`,
    {
      status: InvoiceStatus.Cancelled,
      workLogStatus: request.targetStatus,
      ...(request.comment ? { comment: request.comment } : {})
    }
  );
  return data;
}

export type CancelWorkLogInvoiceRequest = {
  workLogId: string;
  invoiceId: string;
  comment: string;
};

// Payer action: Cancel the linked invoice and reject the work log
export async function cancelWorkLogInvoice(
  request: CancelWorkLogInvoiceRequest
): Promise<IWorkLog> {
  const { data } = await servicePayments.patch(
    `/payable/${request.invoiceId}`,
    {
      status: InvoiceStatus.Cancelled,
      comment: request.comment,
      workLogStatus: "Rejected" as WorkLogStatus
    }
  );

  return data;
}

export type RequestChangesPayeeRequest = {
  workLogId: string;
  comment: string;
};

// Payee action: Request changes on an approved work log
// Stores the payee's change request comment; the client must act to reopen
export async function requestChangesOnApprovedWorkLog(
  request: RequestChangesPayeeRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    payeeOwnedData: { comment: request.comment }
  });
  return data;
}

export type RequestWorkLogEditingRequest = {
  workLogId: string;
  comment: string;
};

// Payee action: request editing on an InReview work log (comment-only, no status change)
// The payer must approve the request and transition InReview → InProgress
export async function requestWorkLogEditing(
  request: RequestWorkLogEditingRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    payeeOwnedData: { comment: request.comment }
  });
  return data;
}

export type CancelEditingRequestPayeeRequest = {
  workLogId: string;
};

// Payee action: cancel a previous editing request on an InReview work log (clears payee comment)
export async function cancelEditingRequest(
  request: CancelEditingRequestPayeeRequest
): Promise<IWorkLog> {
  const { data } = await service.patch(`/${request.workLogId}`, {
    payeeOwnedData: { comment: null }
  });
  return data;
}

export type DeleteWorkLogRequest = {
  workLogId: string;
};

export async function deleteWorkLog(
  request: DeleteWorkLogRequest
): Promise<void> {
  await service.delete(`/${request.workLogId}`);
}
